import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtMoney, fmtMoneyExact, fmtNumber, fmtDate } from '../components/Overtime/format';
import { c, t, sp, r, btn, card, tbl } from '../theme';

// The annual overtime budget, loaded by an admin.
//
// Upload, read, then commit — the same two-step every file input in this portal
// uses, and for the same reason: this file sets the target every variance on the
// L0 board is measured against, and the only moment anyone can tell it is the
// wrong file is before it replaces the current one.
//
// The preview leads with the year's actuals beside the budget total. A budget
// many times larger than the spend it is meant to govern is the signature of a
// broader ledger line pivoted by mistake, and that is invisible once committed —
// every cost center simply looks comfortably under.

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

export default function OvertimeBudgetUpload() {
  const qc = useQueryClient();
  const [file,     setFile]     = useState(null);
  const [year,     setYear]     = useState(CURRENT_YEAR);
  const [progress, setProgress] = useState(0);
  const [preview,  setPreview]  = useState(null);
  const inputRef = useRef(null);

  const { data: batchData, isLoading: batchesLoading } = useQuery({
    queryKey: ['ot-budget-batches'],
    queryFn:  () => api.get('/overtime/budget/batches'),
  });
  const batches = batchData?.batches || [];

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fiscal_year', String(year));
      return api.upload('/overtime/budget/upload', fd, setProgress);
    },
    onSuccess: (data) => { setPreview(data); setProgress(0); },
    onError:   (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/overtime/budget/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(
        data.replaced_batches
          ? `Budget for ${data.fiscal_year} replaced — ${fmtNumber(data.row_count)} cost centers.`
          : `Budget for ${data.fiscal_year} committed — ${fmtNumber(data.row_count)} cost centers.`
      );
      resetAll();
      qc.invalidateQueries({ queryKey: ['ot-budget-batches'] });
      qc.invalidateQueries({ queryKey: ['ot-budget'] });
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/overtime/budget/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['ot-budget-batches'] }); },
    onError:   (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview || null;
  // The comparison that catches a wrong ledger line. Guarded against a year
  // with no payroll behind it yet, where the ratio is meaningless rather than
  // alarming.
  const ratio = p && p.actual_to_date > 0 ? p.total_amount / p.actual_to_date : null;

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Overtime Budget"
        subtitle="The annual overtime budget by cost center and month, as the L0 board reads it"
      />

      <div style={s.card}>
        <div style={s.cardHead}>Load a budget</div>
        <div style={s.body}>
          <p style={s.note}>
            A finance pivot of cost center × month — the Jan…Dec pivot exported from Workday,
            as .csv or .xlsx. <strong>The year is not in the file</strong>, so pick it here: the
            columns are only ever labelled Jan through Dec.
          </p>

          <div style={s.controls}>
            <label style={s.field}>
              <span style={s.fieldLabel}>Budget year</span>
              <select style={s.select} value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={!!preview}>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>

            <label style={s.field}>
              <span style={s.fieldLabel}>File</span>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={s.file}
                disabled={!!preview}
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              />
            </label>

            <button
              style={file && !preview ? btn.primary : s.btnDisabled}
              disabled={!file || !!preview || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}
            >
              <UploadCloud size={14} /> {uploadMutation.isPending ? 'Reading…' : 'Upload and preview'}
            </button>
          </div>

          {progress > 0 && progress < 100 && (
            <div style={s.progressTrack}><div style={{ ...s.progressBar, width: `${progress}%` }} /></div>
          )}
        </div>
      </div>

      {p && (
        <div style={s.card}>
          <div style={s.cardHead}>
            <FileSpreadsheet size={15} /> {p.file_name} — {p.fiscal_year}
          </div>
          <div style={s.body}>
            <div style={s.tiles}>
              <div style={s.tile}>
                <div style={s.tileLabel}>Budget, full year</div>
                <div style={s.tileValue}>{fmtMoneyExact(p.total_amount)}</div>
                <div style={s.tileSub}>{fmtNumber(p.row_count)} cost centers</div>
              </div>
              <div style={s.tile}>
                <div style={s.tileLabel}>Overtime actually spent in {p.fiscal_year}</div>
                <div style={s.tileValue}>{fmtMoneyExact(p.actual_to_date)}</div>
                <div style={s.tileSub}>
                  {p.periods_so_far ? `${fmtNumber(p.periods_so_far)} pay runs to ${fmtDate(p.latest_period_end)}` : 'no payroll loaded yet'}
                </div>
              </div>
              <div style={s.tile}>
                <div style={s.tileLabel}>Budget ÷ spend so far</div>
                <div style={s.tileValue}>{ratio ? `${ratio.toFixed(1)}×` : '—'}</div>
                <div style={s.tileSub}>a whole year against a part year</div>
              </div>
            </div>

            {/* Stated, never enforced. A first year with little payroll loaded
                looks the same as a wrong ledger line, and refusing the upload
                would block the legitimate case with no way round it. */}
            {ratio !== null && ratio > 6 && (
              <div style={s.warn}>
                <AlertTriangle size={14} />
                <span>
                  This budget is {ratio.toFixed(1)}× the overtime recorded so far this year. That is
                  expected early in a year, and it is also what a broader ledger line looks like when
                  it has been pivoted by mistake. Worth confirming this is the overtime line before
                  committing — once loaded, every cost center simply reads as under budget.
                </span>
              </div>
            )}

            {p.replaces && (
              <div style={s.warn}>
                <AlertTriangle size={14} />
                <span>
                  This replaces <strong>{p.replaces.file_name}</strong>, committed{' '}
                  {fmtDate(p.replaces.committed_at)} at {fmtMoney(p.replaces.total_amount)}. The
                  old budget is deleted, not archived.
                </span>
              </div>
            )}

            {p.not_budgeted?.length > 0 && (
              <div style={s.info}>
                {p.not_budgeted.length} cost center{p.not_budgeted.length === 1 ? '' : 's'} in the portal
                have no line in this file: {p.not_budgeted.map((cc) => cc.code).join(', ')}. They will show
                actuals against no budget, which reads as "not budgeted" rather than as an overrun.
              </div>
            )}

            <div style={s.scroll}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Cost Center</th>
                    <th style={s.thR}>Months budgeted</th>
                    <th style={s.thR}>Year total</th>
                  </tr>
                </thead>
                <tbody>
                  {p.cost_centers.map((row) => (
                    <tr key={row.cost_center_code} style={s.tr}>
                      <td style={s.td}>{row.cost_center_code} · {row.cost_center_name}</td>
                      <td style={s.tdR}>
                        {row.month_count === 12
                          ? '12'
                          : <span style={s.partial}>{row.month_count} of 12</span>}
                      </td>
                      <td style={s.tdR}>{fmtMoneyExact(row.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={s.actions}>
              <button style={btn.primary} disabled={confirmMutation.isPending}
                      onClick={() => confirmMutation.mutate()}>
                <CheckCircle size={14} /> {confirmMutation.isPending ? 'Committing…' : `Commit ${p.fiscal_year} budget`}
              </button>
              <button style={btn.secondary} disabled={cancelMutation.isPending}
                      onClick={() => cancelMutation.mutate()}>
                <XCircle size={14} /> Discard
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardHead}>What has been loaded</div>
        {batchesLoading ? <LoadingSpinner /> : !batches.length ? (
          <div style={s.body}><p style={s.note}>Nothing loaded yet.</p></div>
        ) : (
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Year</th>
                  <th style={s.th}>File</th>
                  <th style={s.th}>Status</th>
                  <th style={s.thR}>Cost centers</th>
                  <th style={s.thR}>Total</th>
                  <th style={s.th}>Loaded by</th>
                  <th style={s.th}>Committed</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} style={s.tr}>
                    <td style={s.td}>{b.fiscal_year}</td>
                    <td style={s.td}>{b.file_name}</td>
                    <td style={s.td}>
                      <span style={b.status === 'committed' ? s.chipOn : s.chipOff}>{b.status}</span>
                    </td>
                    <td style={s.tdR}>{fmtNumber(b.row_count)}</td>
                    <td style={s.tdR}>{fmtMoney(b.total_amount)}</td>
                    <td style={s.td}>{b.uploaded_by_email || '—'}</td>
                    <td style={s.td}>{b.committed_at ? fmtDate(b.committed_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  card:     { ...card.base, marginBottom: sp[5], overflow: 'hidden' },
  cardHead: {
    ...t.label, color: c.navy, padding: `${sp[3]} ${sp[5]}`,
    borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', gap: sp[2],
  },
  body:     { padding: sp[5] },
  note:     { ...t.caption, color: c.textMuted, lineHeight: 1.6, margin: `0 0 ${sp[4]}` },
  controls: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: sp[4] },
  field:    { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { ...t.micro, color: c.textFaint },
  select:   {
    fontFamily: 'inherit', fontSize: '13px', padding: '7px 10px',
    border: `1px solid ${c.border}`, borderRadius: r.md, background: c.surface, color: c.text,
  },
  file:     { fontFamily: 'inherit', fontSize: '13px', color: c.textMuted },
  progressTrack: { height: '4px', background: c.surfaceSunk, borderRadius: r.sm, marginTop: sp[4] },
  progressBar:   { height: '100%', background: c.navy, borderRadius: r.sm, transition: 'width .2s' },
  tiles: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: sp[3], marginBottom: sp[4],
  },
  tile:      { ...card.sunk, padding: sp[3] },
  tileLabel: { ...t.micro, color: c.textFaint },
  tileValue: { ...t.h3, color: c.text, marginTop: '2px' },
  tileSub:   { ...t.micro, color: c.textFaint, marginTop: '2px' },
  warn: {
    display: 'flex', gap: sp[2], alignItems: 'flex-start', ...t.caption, color: c.warningText,
    background: c.warningTint, border: `1px solid ${c.warning}`, borderRadius: r.md,
    padding: sp[3], marginBottom: sp[4], lineHeight: 1.6,
  },
  info: {
    ...t.caption, color: c.textMuted, background: c.surfaceSunk,
    border: `1px solid ${c.border}`, borderRadius: r.md, padding: sp[3],
    marginBottom: sp[4], lineHeight: 1.6,
  },
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th, whiteSpace: 'nowrap' },
  thR:    { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:    { ...tbl.tdR },
  partial: { color: c.warningText },
  actions: { display: 'flex', gap: sp[3], marginTop: sp[4] },
  btnDisabled: { ...btn.primary, opacity: 0.5, cursor: 'not-allowed' },
  chipOn:  { ...t.micro, color: c.successText, background: c.successTint, borderRadius: r.pill, padding: '2px 8px' },
  chipOff: { ...t.micro, color: c.textMuted, background: c.surfaceSunk, borderRadius: r.pill, padding: '2px 8px' },
};
